import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRight, RotateCcw, Sparkles } from 'lucide-react';
import { products, quizAnswers } from '@/data/products';
import { QUIZ } from '@/constants/testIds';

const steps = [
  {
    key: 'goal',
    title: 'What is your primary wellness goal?',
    options: [
      { slug: 'sleep', label: 'Better sleep', hint: 'Wind down · Deep rest' },
      { slug: 'stress', label: 'Reduce stress', hint: 'Adaptogens · Calm' },
      { slug: 'digestion', label: 'Support digestion', hint: 'Warmth · Cleanse' },
      { slug: 'hormonal', label: 'Hormonal balance', hint: 'Cycle · Skin' },
      { slug: 'immunity', label: 'Improve immunity', hint: 'Antioxidants · Bright' },
      { slug: 'skin', label: 'Skin health', hint: 'Glow · Clarity' },
    ],
  },
  {
    key: 'time',
    title: 'When do you love your quietest moment?',
    options: [
      { slug: 'morning', label: 'Early morning', hint: 'Fresh light · New start' },
      { slug: 'afternoon', label: 'Afternoon slump', hint: 'A gentle reset' },
      { slug: 'evening', label: 'Late evening', hint: 'Winding down slowly' },
      { slug: 'anytime', label: 'Anytime, really', hint: 'Meet me where I am' },
    ],
  },
  {
    key: 'taste',
    title: 'How do you like your cup?',
    options: [
      { slug: 'floral', label: 'Soft & floral', hint: 'Chamomile · Lavender' },
      { slug: 'spicy', label: 'Warm & spicy', hint: 'Ginger · Turmeric' },
      { slug: 'herby', label: 'Grassy & herbal', hint: 'Nettle · Spearmint' },
      { slug: 'fruity', label: 'Bright & fruity', hint: 'Hibiscus · Pomegranate' },
    ],
  },
];

export default function TeaQuiz() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState(false);

  const progress = ((done ? steps.length : step) / steps.length) * 100;

  const pick = (key, slug) => {
    const next = { ...answers, [key]: slug };
    setAnswers(next);
    if (step < steps.length - 1) {
      setTimeout(() => setStep((s) => s + 1), 300);
    } else {
      setTimeout(() => setDone(true), 350);
    }
  };

  const restart = () => {
    setAnswers({});
    setStep(0);
    setDone(false);
  };

  const matchedSlug = quizAnswers[answers.goal] || 'bedtime';
  const match = products.find((p) => p.slug === matchedSlug);

  return (
    <section
      id="quiz"
      data-testid={QUIZ.root}
      className="relative py-24 md:py-32 overflow-hidden"
    >
      <div className="blob absolute top-10 right-10 w-[400px] h-[400px] rounded-full bg-[#2D8B5B]" style={{ opacity: 0.14 }} />

      <div className="mx-auto max-w-6xl px-6 md:px-10 lg:px-12">
        <div className="text-center max-w-2xl mx-auto">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-xs uppercase tracking-[0.24em] text-[#2D8B5B] mb-4"
          >
            The Sumaya Ritual Quiz
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#1D7448] font-light leading-[1.05]"
          >
            Find Your Perfect <span className="italic">Tea Ritual.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-5 text-[15px] text-[#4A6756]"
          >
            Three gentle questions. One blend made for the way you actually live.
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-14 md:mt-16 rounded-[28px] bg-[#FDFBF7] border border-[#E5DCC3] p-6 md:p-12 shadow-[0_20px_60px_-30px_rgba(13,58,36,0.25)]"
        >
          {/* Progress */}
          <div className="flex items-center justify-between text-xs text-[#4A6756] mb-2">
            <span>
              {done ? 'Your match' : `Step ${step + 1} of ${steps.length}`}
            </span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-[#F3EAD3] overflow-hidden">
            <motion.div
              data-testid={QUIZ.progress}
              className="h-full bg-[#2D8B5B]"
              initial={false}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            />
          </div>

          <div className="mt-10 min-h-[420px]">
            <AnimatePresence mode="wait">
              {!done ? (
                <motion.div
                  key={steps[step].key}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -24 }}
                  transition={{ duration: 0.45 }}
                >
                  <h3 className="font-serif text-2xl md:text-4xl text-[#1D7448] leading-tight max-w-2xl">
                    {steps[step].title}
                  </h3>
                  <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                    {steps[step].options.map((opt) => {
                      const active = answers[steps[step].key] === opt.slug;
                      return (
                        <button
                          key={opt.slug}
                          data-testid={QUIZ.option(opt.slug)}
                          onClick={() => pick(steps[step].key, opt.slug)}
                          className={`text-left p-5 rounded-2xl border transition-all duration-300 hover:-translate-y-0.5 ${
                            active
                              ? 'border-[#1D7448] bg-[#1D7448] text-[#FAF4E3]'
                              : 'border-[#E5DCC3] bg-[#FAF4E3]/60 hover:border-[#1D7448]/60'
                          }`}
                        >
                          <p className={`font-serif text-xl ${active ? 'text-[#FAF4E3]' : 'text-[#1D7448]'}`}>
                            {opt.label}
                          </p>
                          <p className={`text-xs mt-1 ${active ? 'text-[#FAF4E3]/80' : 'text-[#4A6756]'}`}>
                            {opt.hint}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="result"
                  data-testid={QUIZ.result}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.6 }}
                  className="grid md:grid-cols-2 gap-8 items-center"
                >
                  <div className="relative aspect-square md:aspect-[4/5] rounded-[24px] overflow-hidden">
                    <img
                      src={match.image}
                      alt={match.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0D3A24]/50 via-transparent to-transparent" />
                  </div>
                  <div>
                    <div className="inline-flex items-center gap-2 rounded-full border border-[#1D7448]/25 px-4 py-1.5 text-xs uppercase tracking-[0.22em] text-[#1D7448]">
                      <Sparkles className="w-3.5 h-3.5" /> Your match
                    </div>
                    <h3 className="font-serif text-4xl md:text-5xl text-[#1D7448] mt-4 leading-tight">
                      {match.name}
                    </h3>
                    <p className="mt-3 text-[15px] italic text-[#4A6756]">{match.tagline}</p>
                    <p className="mt-6 text-[#4A6756] leading-relaxed">{match.ritual}</p>
                    <div className="flex flex-wrap gap-2 mt-6">
                      {match.benefits.map((b) => (
                        <span
                          key={b}
                          className="text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-full bg-[#F3EAD3] text-[#1D7448]"
                        >
                          {b}
                        </span>
                      ))}
                    </div>
                    <div className="mt-8 flex flex-wrap items-center gap-4">
                      <button
                        data-testid={QUIZ.next}
                        onClick={() => document.getElementById('shop')?.scrollIntoView({ behavior: 'smooth' })}
                        className="btn-shine inline-flex items-center gap-2 bg-[#2D8B5B] hover:bg-[#247149] text-[#FAF4E3] px-6 py-3.5 rounded-full text-sm font-semibold transition-all hover:-translate-y-0.5"
                      >
                        See My Tea Match <ArrowRight className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={QUIZ.restart}
                        onClick={restart}
                        className="inline-flex items-center gap-2 text-[#1D7448] text-sm font-semibold link-sweep"
                      >
                        <RotateCcw className="w-4 h-4" /> Start over
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
